/**
 * Kept Below: storefront behaviour shared by every page.
 *
 * Each feature is a custom element or a guarded init, so one failure can't stop the rest, and
 * custom elements set themselves up again when the theme editor re-renders a section.
 * product.js (product pages only) builds on the helpers exported on window.KeptBelow.
 */

const routes = window.themeRoutes || {};

/** @returns {boolean} */
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let scrollLocks = 0;

/**
 * Locks page scrolling. Counted, so the menu, cart drawer and lightbox can't unlock each other.
 * @returns {() => void} release function (safe to call more than once)
 */
function lockScroll() {
  scrollLocks += 1;
  document.body.classList.add('is-locked');
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('is-locked');
  };
}

/**
 * Reads a message to screen readers through the layout's polite live region.
 * @param {string} message
 */
function announce(message) {
  const region = document.getElementById('A11yAnnouncer');
  if (!region || !message) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 100);
}

/**
 * Makes everything outside `keep` inert, for modal UI that isn't a native <dialog>.
 * @param {HTMLElement} keep
 * @returns {() => void} undo function
 */
function inertOutside(keep) {
  /** @type {HTMLElement[]} */
  const changed = [];
  for (let node = keep; node.parentElement && node !== document.body; node = node.parentElement) {
    for (const sibling of /** @type {HTMLCollectionOf<HTMLElement>} */ (node.parentElement.children)) {
      if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'DIALOG', 'TEMPLATE'].includes(sibling.tagName)) continue;
      sibling.inert = true;
      changed.push(sibling);
    }
  }
  return () => {
    for (const element of changed) element.inert = false;
  };
}

/**
 * Updates every header cart link (badge and accessible name) to a new item count.
 * @param {number} count
 */
function updateCartCount(count) {
  for (const link of document.querySelectorAll(/** @type {'a'} */ ('[data-cart-link]'))) {
    let badge = link.querySelector('.cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cart-count';
        badge.setAttribute('aria-hidden', 'true');
        link.append(badge);
      }
      badge.textContent = String(count);
    } else {
      badge?.remove();
    }
    if (link.dataset.labelTemplate) {
      link.setAttribute('aria-label', link.dataset.labelTemplate.replace('__COUNT__', String(count)));
    }
  }
}

/**
 * @param {string} name
 * @param {CustomElementConstructor} constructor
 */
function define(name, constructor) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

window.KeptBelow = { routes, prefersReducedMotion, lockScroll, announce, inertOutside, updateCartCount, define };

/** Mobile menu: a <details> drawer that behaves as a modal (scroll lock, inert page, Escape). */
class MenuDrawer extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLDetailsElement | null} */
  #details = null;
  #release = () => {};
  #restoreInert = () => {};

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#details = this.querySelector('details');
    if (!this.#details) return;

    this.#details.addEventListener('toggle', () => (this.#details?.open ? this.#onOpen() : this.#onClose()), { signal });
    for (const button of this.querySelectorAll('[data-close-drawer]')) {
      button.addEventListener('click', () => this.close(), { signal });
    }
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.#details?.open) return;
        event.stopPropagation();
        this.close();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#onClose();
  }

  close() {
    if (!this.#details) return;
    this.#details.open = false;
    this.#details.querySelector('summary')?.focus();
  }

  #onOpen() {
    this.#release = lockScroll();
    this.#restoreInert = inertOutside(this);
  }

  #onClose() {
    this.#release();
    this.#restoreInert();
    this.#release = () => {};
    this.#restoreInert = () => {};
  }
}

/** Header dropdown: closes on Escape, outside click and when focus leaves it. */
class DetailsDropdown extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;

    document.addEventListener(
      'click',
      (event) => {
        if (details.open && !this.contains(/** @type {Node} */ (event.target))) details.open = false;
      },
      { signal },
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        details.open = false;
        summary.focus();
      },
      { signal },
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (details.open && event.relatedTarget && !this.contains(/** @type {Node} */ (event.relatedTarget))) details.open = false;
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/** Plus/minus buttons around a number input; emits a normal `change` event. */
class QuantityInput extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => this.#step(event), { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #step(event) {
    const button = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('[data-qty-step]'));
    const input = this.querySelector('input');
    if (!button || !input) return;
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(input.value || 0) + Number(button.dataset.qtyStep)));
    if (next === Number(input.value)) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * Cart drawer: a native <dialog>. Line changes are debounced per line, sent one at a time
 * (so a slow response can never overwrite a newer one) and addressed by line key, not position.
 */
class CartDrawer extends HTMLElement {
  #controller = new AbortController();
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {Promise<void>} */
  #queue = Promise.resolve();
  /** @type {Map<string, number>} */
  #timers = new Map();
  #release = () => {};
  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    document.addEventListener(
      'click',
      (event) => {
        const link = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest('[data-cart-link]'));
        if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      },
      { signal },
    );
    document.addEventListener(
      'cart:updated',
      (event) => {
        const { sections, open, trigger } = /** @type {CustomEvent} */ (event).detail || {};
        this.#render(sections?.['cart-drawer']);
        if (open) this.open(trigger);
      },
      { signal },
    );
    this.#dialog.addEventListener('close', () => this.#onClose(), { signal });
    this.#dialog.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.#dialog.addEventListener('change', (event) => this.#onChange(event), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#release();
  }

  /** @param {HTMLElement | null} [trigger] element to return focus to on close */
  open(trigger = /** @type {HTMLElement | null} */ (document.activeElement)) {
    if (!this.#dialog || this.#dialog.open) return;
    this.#trigger = trigger;
    this.#dialog.showModal();
    this.#release = lockScroll();
  }

  close() {
    this.#dialog?.close();
  }

  #onClose() {
    this.#release();
    this.#release = () => {};
    if (this.#trigger?.isConnected) this.#trigger.focus();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = /** @type {Element} */ (event.target);
    if (target === this.#dialog || target.closest('[data-drawer-close]')) {
      this.close();
      return;
    }
    const remove = target.closest('[data-line-remove]');
    const key = remove?.closest('[data-line-key]')?.getAttribute('data-line-key');
    if (key) this.#change(key, 0);
  }

  /** @param {Event} event */
  #onChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches('[data-line-qty]')) return;
    const key = input.closest('[data-line-key]')?.getAttribute('data-line-key');
    if (!key) return;
    window.clearTimeout(this.#timers.get(key));
    this.#timers.set(
      key,
      window.setTimeout(() => {
        this.#timers.delete(key);
        this.#change(key, Number(input.value));
      }, 350),
    );
  }

  /**
   * @param {string} key
   * @param {number} quantity
   */
  #change(key, quantity) {
    this.#queue = this.#queue.then(() => this.#request(key, quantity));
  }

  /**
   * @param {string} key
   * @param {number} quantity
   */
  async #request(key, quantity) {
    const inner = this.#dialog?.querySelector('.cart-drawer__inner');
    const focus = this.#focusState();
    inner?.classList.add('is-loading');
    try {
      const response = await fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || '');
      this.#render(json.sections?.['cart-drawer'], focus);
      announce(this.dataset.labelUpdated || '');
    } catch (error) {
      inner?.classList.remove('is-loading');
      this.#showError((error instanceof Error && error.message) || this.dataset.labelError || '');
    }
  }

  /**
   * Swaps in freshly rendered drawer markup and puts keyboard focus back where it was.
   * @param {string | undefined} html
   * @param {{ key?: string, selector?: string } | null} [focus]
   */
  #render(html, focus = null) {
    if (!html || !this.#dialog) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer__inner');
    const current = this.#dialog.querySelector('.cart-drawer__inner');
    if (!fresh || !current) return;
    current.replaceWith(fresh);
    updateCartCount(Number(fresh.getAttribute('data-cart-count') || 0));
    if (!focus) return;
    const line = focus.key ? fresh.querySelector(`[data-line-key="${CSS.escape(focus.key)}"]`) : null;
    const target = line && focus.selector ? line.querySelector(focus.selector) : null;
    /** @type {HTMLElement | null} */ (target ?? fresh.querySelector('#CartDrawerTitle'))?.focus();
  }

  /** @returns {{ key?: string, selector?: string } | null} */
  #focusState() {
    const active = document.activeElement;
    if (!active || !this.#dialog?.contains(active)) return null;
    const selector = ['[data-line-qty]', '[data-qty-step="1"]', '[data-qty-step="-1"]', '[data-line-remove]'].find((s) => active.matches(s));
    return { key: active.closest('[data-line-key]')?.getAttribute('data-line-key') ?? undefined, selector };
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.#dialog?.querySelector('[data-drawer-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }
}

/** Cart page: submits the cart form (updating quantities) shortly after a quantity changes. */
class CartForm extends HTMLElement {
  #controller = new AbortController();
  #timer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener(
      'change',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (!target.matches('[name="updates[]"]')) return;
        window.clearTimeout(this.#timer);
        this.#timer = window.setTimeout(() => this.querySelector('form')?.requestSubmit(), 450);
      },
      { signal: this.#controller.signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearTimeout(this.#timer);
  }
}

/** Submits the surrounding form when the wrapped control changes (collection sorting). */
class AutoSubmit extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('change', () => this.closest('form')?.requestSubmit(), { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/**
 * Long section text cut to a few lines (CSS line clamp) with a Read more / Read less toggle.
 * Beside an image-with-text photo (wide screens) the text shows in full in its column; stacked
 * under the photo (phones) it is cut to the section's line count. Without JS it shows in full.
 */
class ReadMore extends HTMLElement {
  #controller = new AbortController();
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const text = /** @type {HTMLElement | null} */ (this.querySelector('[data-read-more-text]'));
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-read-more-toggle]'));
    const label = button?.querySelector('[data-read-more-label]');
    if (!text || !button || !label) return;
    const content = this.closest('.split__content');
    const media = content?.closest('.split')?.querySelector('.split__media-inner');

    const fit = () => {
      if (this.classList.contains('is-expanded')) return;
      const beside = !!content && !!media && content.getBoundingClientRect().top < media.getBoundingClientRect().bottom;
      this.classList.toggle('is-whole', beside);
      button.hidden = beside || text.scrollHeight <= text.clientHeight + 1;
    };
    // The photo's size follows the width only, so watching it can't feed back into the fit.
    this.#observer = new ResizeObserver(fit);
    this.#observer.observe(media || text);
    // Web fonts swap in after first paint and reflow the text without resizing the photo.
    document.fonts.ready.then(() => this.isConnected && fit());

    button.addEventListener(
      'click',
      () => {
        const expanded = this.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        label.textContent = (expanded ? button.dataset.less : button.dataset.more) || '';
        if (!expanded) button.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      },
      { signal: this.#controller.signal },
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }
}

define('menu-drawer', MenuDrawer);
define('details-dropdown', DetailsDropdown);
define('quantity-input', QuantityInput);
define('cart-drawer', CartDrawer);
define('cart-form', CartForm);
define('auto-submit', AutoSubmit);
define('read-more', ReadMore);

/**
 * Runs page-level setup in isolation, so a failure in one feature is logged and the rest still work.
 * @param {string} name
 * @param {() => void} setup
 */
function init(name, setup) {
  try {
    setup();
  } catch (error) {
    console.error(`[Kept Below] ${name} failed`, error);
  }
}

init('reveal', () => {
  const root = document.documentElement;
  const show = (/** @type {Element} */ element) => element.classList.add('is-visible');
  const elements = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          show(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    for (const element of elements) observer.observe(element);
  } else {
    elements.forEach(show);
  }
  root.classList.add('reveal-ready');
  document.addEventListener('shopify:section:load', (event) => {
    /** @type {Element} */ (event.target).querySelectorAll('[data-reveal]').forEach(show);
  });
});

init('transparent header', () => {
  const header = document.querySelector('.site-header--transparent');
  if (!header) return;
  const update = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
  update();
  window.addEventListener('scroll', update, { passive: true });
});

init('instagram shuffle', () => {
  for (const grid of document.querySelectorAll('[data-insta-grid][data-shuffle]')) {
    const items = [...grid.querySelectorAll('[data-insta-item]')];
    const count = Number(/** @type {HTMLElement} */ (grid).dataset.count || 5);
    if (items.length <= count) continue;
    const shuffled = items
      .map((item) => /** @type {[number, Element]} */ ([Math.random(), item]))
      .sort((a, b) => a[0] - b[0])
      .map(([, item]) => item);
    for (const item of items) item.classList.add('is-hidden');
    for (const item of shuffled.slice(0, count)) item.classList.remove('is-hidden');
  }
});

init('contact prefill', () => {
  const product = new URLSearchParams(window.location.search).get('product');
  if (!product) return;
  for (const textarea of document.querySelectorAll('textarea[data-similar-template]')) {
    const field = /** @type {HTMLTextAreaElement} */ (textarea);
    if (!field.value.trim()) field.value = (field.dataset.similarTemplate || '').replace('__PRODUCT__', product);
  }
});

export {};
